import type { IAppProps } from '@/interfaces/IAppProps'
import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client/core'
import { NamiApiRegister } from '@nami/apis'
import { createLazyIcon } from '@sbo/icons-vue'
import { provideApolloClient } from '@vue/apollo-composable'
import { createHead } from '@vueuse/head'
import { createPinia } from 'pinia'
import { createApp, h, watch } from 'vue'
import { createDatasetProps } from '@/composable/useDatasetProps'
import { createLogger } from '@/composable/useLogger'
import { ResourceManagementFactory } from '@/plugins/i18n'
import { useLanguageStore } from '@/stores/languageStore'
import App from './App.vue'
import router from './router'
import './utils/extensions'
import './polyfill'

const URL = import.meta.env.VITE_DOMAIN

const mountId = '#app'
const resources = import.meta.glob('@/locales/*.json', { query: 'raw' })

async function initApp() {
    const httpLink = createHttpLink({
        uri: URL,
    })
    const cache = new InMemoryCache()
    const apolloClient = new ApolloClient({
        link: httpLink,
        cache,
    })

    const app = createApp({
        setup() {
            provideApolloClient(apolloClient)
        },

        render: () => h(App),
    })
    const head = createHead()
    app.use(createPinia())

    app.use(router)
    app.use(head)

    const entryProps = createDatasetProps<IAppProps>(mountId, {
        loginName: 'any_account',
        depositUrl: '',
        liveChatUrl: '',
        homeUrl: '',
        brandName: 'SBOTOP',
        termsAndConditionsUrl: '',
        isApp: false,
        refererDomain: '',
    })
    app.use(entryProps)

    const languageStore = useLanguageStore()
    const languageManagement = await ResourceManagementFactory.createResourceManagementAsync(
        resources,
        languageStore.language,
    )
    app.use(languageManagement.getI18nInstance())
    languageStore.setI18nInstance(languageManagement.getI18nInstance())
    watch(
        () => languageStore.language,
        (newLanguage) => {
            languageManagement.updateLanguageAsync(newLanguage)
        },
    )

    // Use the new unified NamiApiRegister plugin
    app.use(NamiApiRegister, {
        apiConfig: {
            clientType: 'Nami',
            homeUrl: entryProps.state.homeUrl,
            loginName: entryProps.state.loginName,
            router,
        },
        logger: {
            pageName: 'PaymentPage',
            allowLevels: import.meta.env.PROD ? ['error', 'information'] : ['error', 'information', 'debug'],
            enableConsole: import.meta.env.DEV,
            enableApi: !import.meta.env.DEV,
            refererDomain: entryProps.state.refererDomain,
        },
    })

    app.use(
        createLogger({
            pageName: 'PaymentPage',
            allowLevels: import.meta.env.PROD ? ['error', 'information'] : ['error', 'information', 'debug'],
            enableConsole: import.meta.env.DEV,
            enableApi: !import.meta.env.DEV,
            refererDomain: entryProps.state.refererDomain,
        }),
    )

    app.use(createLazyIcon())
    // if need custom class name, can use this setting when use sbo icon system
    // app.use(registerIconClassName('custom_icon_class_name'))

    return app
}

initApp().then((app) => {
    app.mount(mountId)
})
